/**
 * PushSettingsCard — "Notificaciones en este dispositivo" (PUSH Fase 1A).
 *
 * Estados (G): No configuradas · No compatibles · iOS sin PWA
 * (instrucciones, sin forzar prompt) · No permitidas (botón Activar) ·
 * Bloqueadas por el navegador · Activadas (con Desactivar en este
 * dispositivo). El permiso SOLO se pide tras click explícito. Además:
 * toggle de cuenta (users.push_enabled) y, para admin, botón de
 * notificación de prueba (siempre dirigida al propio admin).
 */
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { sendTestPush, setPushPreference } from "@/lib/pushApi";
import {
  getPushState, requestPushPermission, setDeviceOptedOut,
} from "@/lib/pushNotifications";
import { BellRinging, DeviceMobile } from "@phosphor-icons/react";

export default function PushSettingsCard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [state, setState] = useState(null); // null = cargando
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null); // {tone:'ok'|'error', text}

  const refresh = useCallback(async () => {
    try {
      setState(await getPushState());
    } catch {
      setState({ supported: false });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const activate = async () => {
    if (busy) return;
    setBusy(true); setNotice(null);
    try {
      const granted = await requestPushPermission(user?.user_id);
      if (!granted) {
        setNotice({ tone: "error", text: "No se concedió el permiso de notificaciones." });
      }
    } catch {
      setNotice({ tone: "error", text: "No se pudieron activar las notificaciones." });
    } finally {
      setBusy(false);
      refresh();
    }
  };

  const deactivateDevice = async () => {
    if (busy) return;
    setBusy(true); setNotice(null);
    try {
      await setDeviceOptedOut(true);
    } catch { /* best-effort */ } finally {
      setBusy(false);
      refresh();
    }
  };

  const toggleAccount = async () => {
    if (busy || !state) return;
    setBusy(true); setNotice(null);
    try {
      const next = !(state.accountEnabled !== false);
      await setPushPreference(next);
      setState((s) => (s ? { ...s, accountEnabled: next } : s));
    } catch {
      setNotice({ tone: "error", text: "No se pudo guardar la preferencia." });
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    if (busy) return;
    setBusy(true); setNotice(null);
    try {
      const r = await sendTestPush();
      setNotice(r?.ok
        ? { tone: "ok", text: "Notificación de prueba enviada a este usuario." }
        : { tone: "error", text: "El proveedor no pudo entregar la prueba." });
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      setNotice({
        tone: "error",
        text: code === "push_disabled"
          ? "Las notificaciones push no están configuradas en este entorno."
          : "No se pudo enviar la notificación de prueba.",
      });
    } finally {
      setBusy(false);
    }
  };

  let statusLabel = "Cargando…";
  let body = null;
  if (state) {
    if (!state.supported) {
      statusLabel = "No compatibles";
      body = (
        <p className="text-xs text-stone-500">
          Este navegador no soporta notificaciones push web.
        </p>
      );
    } else if (!state.configured) {
      statusLabel = "No configuradas";
      body = (
        <p className="text-xs text-stone-500" data-testid="push-not-configured">
          Las notificaciones push no están configuradas en este entorno.
        </p>
      );
    } else if (state.iosNeedsInstall) {
      statusLabel = "Requiere pantalla de inicio";
      body = (
        <div
          className="text-xs text-stone-600 dark:text-stone-300 flex items-start gap-1.5"
          data-testid="push-ios-instructions"
        >
          <DeviceMobile size={15} className="shrink-0 mt-0.5" />
          <span>
            Añade Dolce a la pantalla de inicio para activar notificaciones:
            en Safari, toca <strong>Compartir</strong> →{" "}
            <strong>Añadir a pantalla de inicio</strong> y abre la app desde ahí.
          </span>
        </div>
      );
    } else if (state.permission === "denied") {
      statusLabel = "Bloqueadas por el navegador";
      body = (
        <p className="text-xs text-stone-500">
          El navegador tiene bloqueadas las notificaciones para este sitio.
          Permítelas en los ajustes del navegador y recarga.
        </p>
      );
    } else if (state.permission === "granted" && state.optedIn) {
      statusLabel = "Activadas";
      body = (
        <button type="button" onClick={deactivateDevice} disabled={busy}
                className="dv-btn dv-btn-ghost text-xs"
                data-testid="push-deactivate-device">
          Desactivar en este dispositivo
        </button>
      );
    } else {
      statusLabel = "No permitidas";
      body = (
        <button type="button" onClick={activate} disabled={busy}
                className="dv-btn dv-btn-primary text-xs inline-flex items-center gap-1.5"
                data-testid="push-activate">
          <BellRinging size={14} weight="bold" />
          {busy ? "Activando…" : "Activar notificaciones"}
        </button>
      );
    }
  }

  return (
    <div className="dv-card space-y-3" data-testid="push-settings-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 mb-1">
            Notificaciones en este dispositivo
          </div>
          <div className="font-medium" data-testid="push-status-label">{statusLabel}</div>
        </div>
        {state?.configured && (
          <label className="text-xs text-stone-500 inline-flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={state.accountEnabled !== false}
                   onChange={toggleAccount} disabled={busy}
                   data-testid="push-account-toggle" />
            Recibir notificaciones en esta cuenta
          </label>
        )}
      </div>
      {notice && (
        <div className={`text-xs rounded px-3 py-2 border ${
          notice.tone === "ok"
            ? "text-emerald-800 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-800"
            : "text-red-700 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-950/40 dark:border-red-800"
        }`} data-testid="push-notice">
          {notice.text}
        </div>
      )}
      {body}
      {isAdmin && state?.configured && (
        <div className="pt-1 border-t border-stone-100 dark:border-stone-800">
          <button type="button" onClick={sendTest} disabled={busy}
                  className="dv-btn dv-btn-ghost text-xs"
                  data-testid="push-send-test">
            Enviar notificación de prueba
          </button>
        </div>
      )}
    </div>
  );
}
